// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PinInput } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Anchor, Badge, Box, Button, Center, Container, Stack, Text, Title } from '@/components/ui';
import { IconArrowLeft, IconDeviceMobile, Icon3dCubeSphere, IconCheck } from '@tabler/icons-react';
import { FadeIn } from '@/components/animations';
import { Floating3DCard } from '../components';

export default function OtpMinimal3D() {
  const [otp, setOtp] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleChange = (value: string) => {
    setOtp(value);
    setIsComplete(value.length === 6);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerified(true);
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: isDark
          ? `linear-gradient(180deg, ${theme.colors.dark[8]} 0%, ${theme.colors.dark[9]} 100%)`
          : `linear-gradient(180deg, ${theme.colors.gray[0]} 0%, ${theme.colors.gray[1]} 100%)`,
        position: 'relative',
      }}
    >
      {/* Subtle pattern */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(${isDark ? theme.colors.dark[6] : theme.colors.gray[3]} 1px, transparent 1px)`,
          backgroundSize: '32px 32px',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="xl" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <FadeIn delay={100}>
            <Floating3DCard
              maxTilt={10}
              floatingShadow
              glow
              glowColor={theme.colors.violet[5]}
              radius="lg"
            >
              {isVerified ? (
                <Stack gap="lg" align="center">
                  <Center
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 20,
                      backgroundColor: 'var(--mantine-color-green-light)',
                    }}
                  >
                    <IconCheck size={40} color="var(--mantine-color-green-6)" />
                  </Center>

                  <Stack align="center" gap="xs">
                    <Title order={2} ta="center">Verified!</Title>
                    <Text c="dimmed" ta="center" maw={300}>
                      Your identity has been successfully verified.
                    </Text>
                  </Stack>

                  <Button
                    component={Link}
                    to="/demo/auth-pages/login/minimal3d"
                    fullWidth
                    size="md"
                    radius="md"
                    color="dark"
                  >
                    Continue
                  </Button>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/demo/auth-pages/otp"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <Stack align="center" gap="xs">
                    <Badge
                      leftSection={<Icon3dCubeSphere size={12} />}
                      variant="gradient"
                      gradient={{ from: 'gray', to: 'dark' }}
                      size="lg"
                    >
                      Minimal 3D
                    </Badge>
                    <Center
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: 14,
                        backgroundColor: 'var(--mantine-color-violet-light)',
                        marginTop: 'var(--mantine-spacing-sm)',
                      }}
                    >
                      <IconDeviceMobile size={28} color="var(--mantine-color-violet-6)" />
                    </Center>
                    <Title order={2} ta="center" mt="sm">
                      Verification code
                    </Title>
                    <Text c="dimmed" size="sm" ta="center" maw={300}>
                      We've sent a 6-digit code to your phone ending in **42
                    </Text>
                  </Stack>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="lg">
                      <Center>
                        <PinInput
                          length={6}
                          size="lg"
                          value={otp}
                          onChange={handleChange}
                          type="number"
                          oneTimeCode
                          aria-label="Verification code"
                        />
                      </Center>

                      <Button
                        type="submit"
                        fullWidth
                        size="md"
                        radius="md"
                        color="dark"
                        disabled={!isComplete}
                      >
                        Verify code
                      </Button>
                    </Stack>
                  </form>

                  <Text ta="center" size="sm" c="dimmed">
                    Didn't receive the code?{' '}
                    <Anchor component="button" type="button" fw={500}>
                      Resend
                    </Anchor>
                  </Text>

                  <Center>
                    <Anchor component={Link} to="/demo/auth-pages/login/minimal3d" size="sm">
                      <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Back to login
                    </Anchor>
                  </Center>
                </Stack>
              )}
            </Floating3DCard>
          </FadeIn>
        </Container>
      </Center>
    </Box>
  );
}
